import React, { useEffect, useState } from 'react';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import API from '../api';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';

const ROLES = [
  { value: 'admin', label: 'Admin', hint: 'Full access, including Site Settings and other admin accounts.' },
  { value: 'editor', label: 'Editor', hint: 'Can manage Hero Slides, Categories, Products, Clients and Messages. Cannot open Site Settings or manage accounts.' },
];

const EMPTY_FORM = { username: '', password: '', role: 'editor' };

export default function AdminUsers() {
  const { admin: currentAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { mode: 'add' } | { mode: 'edit', user }
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => API.get('/admins').then(r => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const adminCount = users.filter(u => u.role === 'admin').length;

  const openAdd = () => { setForm(EMPTY_FORM); setError(''); setModal({ mode: 'add' }); };
  const openEdit = (user) => { setForm({ username: user.username, password: '', role: user.role }); setError(''); setModal({ mode: 'edit', user }); };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await API.post('/admins', form);
      setModal(null);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to create account'); }
    setSaving(false);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { username: form.username, role: form.role };
      if (form.password) payload.password = form.password;
      await API.put(`/admins/${modal.user.id}`, payload);
      setModal(null);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save changes'); }
    setSaving(false);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Remove admin account "${user.username}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/admins/${user.id}`);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete account'); }
  };

  return (
    <div className="bg-white border border-gray-100 shadow-sm p-6">
      <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-5">
        <div>
          <h2 className="font-semibold text-gray-700 text-sm uppercase tracking-wider">Admin Accounts</h2>
          <p className="text-xs text-gray-400 mt-1">Control who can log in and what they can access.</p>
        </div>
        <button onClick={openAdd} className="btn-primary text-sm flex items-center gap-2">
          <PlusIcon className="w-4 h-4" /> Add Account
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Loading...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead>
              <tr className="border-b border-gray-100">
                {['Email', 'Role', 'Created', ''].map(h => (
                  <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map(u => {
                const isSelf = u.id === currentAdmin?.id;
                const isLastAdmin = u.role === 'admin' && adminCount <= 1;
                return (
                  <tr key={u.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-3 py-3">
                      <span className="font-medium text-gray-800">{u.username}</span>
                      {isSelf && <span className="ml-2 text-xs text-accent">(you)</span>}
                    </td>
                    <td className="px-3 py-3 text-gray-600">{ROLES.find(r => r.value === u.role)?.label || u.role}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs">{u.created_at?.slice(0, 10)}</td>
                    <td className="px-3 py-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => openEdit(u)}
                          className="p-1.5 text-gray-400 hover:text-primary border border-gray-200 hover:border-primary transition-colors"
                          title="Edit">
                          <PencilIcon className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDelete(u)}
                          disabled={isSelf || isLastAdmin}
                          title={isSelf ? "You can't delete your own account" : isLastAdmin ? 'Cannot delete the last remaining admin' : 'Delete'}
                          className="p-1.5 text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-300 transition-colors disabled:opacity-30 disabled:hover:text-gray-400 disabled:hover:border-gray-200">
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal?.mode === 'add' && (
        <Modal title="Add Admin Account" onClose={() => setModal(null)}>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email Address</label>
              {/* type="text": the backend accepts any identifier, and strict
                  HTML5 email validation would block editing an existing
                  account whose login isn't already email-shaped. */}
              <input type="text" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                required placeholder="name@sankyomt.ph"
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              <p className="text-xs text-gray-400 mt-1">Used to log in — doesn't need to be a real inbox, but an email keeps accounts easy to tell apart.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Password</label>
              <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                required minLength={6} className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              <p className="text-xs text-gray-400 mt-1">At least 6 characters.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Role</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary">
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <p className="text-xs text-gray-400 mt-1">{ROLES.find(r => r.value === form.role)?.hint}</p>
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving} className="btn-primary text-sm disabled:opacity-60 flex-1">
                {saving ? 'Creating...' : 'Create Account'}
              </button>
              <button type="button" onClick={() => setModal(null)} className="btn-outline text-sm flex-1">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {modal?.mode === 'edit' && (
        <Modal title={`Edit Account — ${modal.user.username}`} onClose={() => setModal(null)}>
          <form onSubmit={handleEditSave} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email Address</label>
              {/* type="text": the backend accepts any identifier, and strict
                  HTML5 email validation would block editing an existing
                  account whose login isn't already email-shaped. */}
              <input type="text" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                required placeholder="name@sankyomt.ph"
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Role</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                disabled={modal.user.role === 'admin' && adminCount <= 1}
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary disabled:bg-gray-50 disabled:text-gray-400">
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              {modal.user.role === 'admin' && adminCount <= 1 ? (
                <p className="text-xs text-gray-400 mt-1">This is the last remaining admin, so its role can't be changed.</p>
              ) : (
                <p className="text-xs text-gray-400 mt-1">{ROLES.find(r => r.value === form.role)?.hint}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">New Password</label>
              <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                minLength={6} placeholder="Leave blank to keep the current password"
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              <p className="text-xs text-gray-400 mt-1">This does not require their current password.</p>
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving} className="btn-primary text-sm disabled:opacity-60 flex-1">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button type="button" onClick={() => setModal(null)} className="btn-outline text-sm flex-1">Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
